import type { OrchestrationEvent, OrchestrationThreadDetail, ThreadId } from "@t3tools/contracts";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Throttler } from "@tanstack/react-pacer";
import { useEffect, useRef, useState } from "react";

import { toastManager } from "../components/ui/toast";
import { clearPromotedDraftThreads, useComposerDraftStore } from "../composerDraftStore";
import { isOperatorsIndexPathname } from "../operatorsRoutes";
import { operatorsSessionQueryOptions, operatorsQueryKeys } from "../lib/operatorsReactQuery";
import { projectQueryKeys } from "../lib/projectReactQuery";
import { providerQueryKeys } from "../lib/providerReactQuery";
import { collectActiveTerminalThreadIds } from "../lib/terminalStateCleanup";
import { asThreadId } from "../lib/utils";
import { readNativeApi } from "../nativeApi";
import { createThreadNotificationPresence } from "../threadNotificationPresence";
import { useOperatorsAuth } from "../operatorsAuth";
import {
  createThreadCompletionTracker,
  createThreadAttentionTracker,
  createThreadNotificationQueue,
} from "../threadCompletionNotifications";
import { notifyOperatorsAuthExpired } from "../operatorsAuthSession";
import { useStore } from "../store";
import { useTerminalStateStore } from "../terminalStateStore";
import {
  closeOperatorsThread,
  getOperatorsThreadIndex,
  onServerWelcome,
  openOperatorsThread,
} from "../wsNativeApi";
import { WsRequestError } from "../wsTransport";
import {
  isIncrementalThreadEvent,
  mergeOrchestrationEvents,
  routeActiveThreadEvent,
} from "./-rootEventRouter.logic";

const SYNC_RETRY_INITIAL_MS = 250;
const SYNC_RETRY_MAX_MS = 5_000;
const MAX_BUFFERED_THREAD_EVENTS = 512;

function isTerminalSyncError(error: unknown): error is WsRequestError {
  return (
    error instanceof WsRequestError &&
    (error.code === "forbidden" || error.code === "not_found" || error.code === "unauthorized")
  );
}

function nextRetryDelay(current: number): number {
  return Math.min(current * 2, SYNC_RETRY_MAX_MS);
}

export function OrchestrationSync() {
  return (
    <>
      <ShellIndexCoordinator />
      <ActiveThreadCoordinator />
    </>
  );
}

function ShellIndexCoordinator() {
  const { user } = useOperatorsAuth();
  const currentUserEmail = user?.email;
  const syncThreadIndex = useStore((store) => store.syncThreadIndex);
  const setProjectExpanded = useStore((store) => store.setProjectExpanded);
  const removeOrphanedTerminalStates = useTerminalStateStore(
    (store) => store.removeOrphanedTerminalStates,
  );
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const pathnameRef = useRef(pathname);
  const handledBootstrapThreadIdRef = useRef<string | null>(null);
  pathnameRef.current = pathname;
  const presenceRef = useRef<ReturnType<typeof createThreadNotificationPresence> | null>(null);
  useEffect(() => {
    presenceRef.current?.refresh();
  }, [pathname]);

  useEffect(() => {
    const api = readNativeApi();
    if (!api) return;

    let disposed = false;
    let terminal = false;
    let debt = false;
    let serverGeneration = 0;
    const invalidatedThreadIds = new Set<ThreadId>();
    let activeSync: Promise<void> | null = null;
    let retryDelayMs = SYNC_RETRY_INITIAL_MS;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const abortController = new AbortController();
    const knownSummaries = new Map<
      ThreadId,
      Awaited<ReturnType<typeof getOperatorsThreadIndex>>["threads"][number]
    >();
    const trackCompletions = createThreadCompletionTracker();
    const attention = createThreadAttentionTracker();
    const attentionToasts = new Map<string, { id: string; isPending: () => boolean }>();
    const notifications = createThreadNotificationQueue();
    const presence = currentUserEmail
      ? createThreadNotificationPresence(currentUserEmail, () =>
          document.visibilityState === "visible" && document.hasFocus()
            ? pathnameRef.current
            : null,
        )
      : null;
    presenceRef.current = presence;
    window.addEventListener("online", notifications.retryPending);
    window.addEventListener("focus", notifications.retryPending);

    const cancelRetry = () => {
      if (retryTimer !== null) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
    };

    const applyIndex = (
      index: Awaited<ReturnType<typeof getOperatorsThreadIndex>>,
      updateCursor = true,
    ) => {
      if (index.revision < useStore.getState().threadIndexRevision) return;
      syncThreadIndex(index, { updateCursor });
      if (index.nextCursor === undefined) knownSummaries.clear();
      for (const thread of index.threads) knownSummaries.set(thread.id, thread);
      const notificationThreads = [...knownSummaries.values()];
      const attentionCandidates = attention.update(notificationThreads);
      for (const [key, toast] of attentionToasts) {
        if (!toast.isPending()) {
          toastManager.close(toast.id);
          attentionToasts.delete(key);
        }
      }
      const candidates = [
        ...trackCompletions(notificationThreads, index.updatedAt).map((thread) => ({
          thread,
          request: undefined,
        })),
        ...attentionCandidates,
      ];
      for (const { thread, request } of candidates) {
        if (!currentUserEmail) continue;
        notifications.enqueue({
          email: currentUserEmail,
          thread,
          isActive: () => !disposed,
          ...(request
            ? {
                request,
                canDeliver: () => attention.isPending(thread, request),
                canShowInApp: () => document.visibilityState === "visible" && document.hasFocus(),
                showInApp: () => {
                  const key = JSON.stringify([
                    thread.id,
                    thread.latestTurn?.turnId,
                    request.kind,
                    request.requestId,
                  ]);
                  const id = toastManager.add({
                    type: "info",
                    title:
                      request.kind === "approval"
                        ? "Agent needs approval"
                        : "Agent needs your input",
                    description: thread.title,
                    timeout: 0,
                    actionProps: {
                      children: "Open thread",
                      onClick: () => {
                        if (disposed) return;
                        toastManager.close(id);
                        void navigate({
                          to: "/operators/$threadId",
                          params: { threadId: thread.id },
                        });
                      },
                    },
                  });
                  attentionToasts.set(key, {
                    id,
                    isPending: () => attention.isPending(thread, request),
                  });
                },
              }
            : {}),
          isViewingThread: () => presence?.isThreadViewed(thread.id) ?? false,
          loadSession: () =>
            queryClient.fetchQuery({
              ...operatorsSessionQueryOptions({ sessionId: thread.id }),
              staleTime: 0,
            }),
          openThread: () => {
            void navigate({ to: "/operators/$threadId", params: { threadId: thread.id } });
          },
        });
      }
      clearPromotedDraftThreads(new Set(index.threads.map((thread) => thread.id)));
      const draftThreadIds = Object.keys(
        useComposerDraftStore.getState().draftThreadsByThreadId,
      ) as ThreadId[];
      if (index.nextCursor === undefined) {
        removeOrphanedTerminalStates(
          collectActiveTerminalThreadIds({ snapshotThreads: index.threads, draftThreadIds }),
        );
      }
    };

    const handleTerminalError = (error: WsRequestError) => {
      terminal = true;
      debt = false;
      cancelRetry();
      if (error.code === "unauthorized") {
        notifyOperatorsAuthExpired(error.message);
      }
    };

    const runSync = async (): Promise<void> => {
      if (activeSync || disposed || terminal) return await (activeSync ?? Promise.resolve());
      activeSync = (async () => {
        while (debt) {
          if (disposed || terminal) return;
          debt = false;
          try {
            const requestedGeneration = serverGeneration;
            const index = await getOperatorsThreadIndex({ signal: abortController.signal });
            if (disposed) return;
            if (requestedGeneration !== serverGeneration) {
              debt = true;
              continue;
            }
            applyIndex(index);
            while (invalidatedThreadIds.size > 0) {
              const threadIds = [...invalidatedThreadIds].slice(0, 100);
              for (const id of threadIds) invalidatedThreadIds.delete(id);
              let summaries;
              try {
                summaries = await getOperatorsThreadIndex({
                  threadIds,
                  signal: abortController.signal,
                });
              } catch (error) {
                for (const id of threadIds) invalidatedThreadIds.add(id);
                throw error;
              }
              if (disposed) return;
              if (requestedGeneration !== serverGeneration) {
                for (const id of threadIds) invalidatedThreadIds.add(id);
                debt = true;
                break;
              }
              if (summaries.revision < useStore.getState().threadIndexRevision) {
                for (const id of threadIds) invalidatedThreadIds.add(id);
                debt = true;
                break;
              }
              applyIndex(summaries, false);
            }
            retryDelayMs = SYNC_RETRY_INITIAL_MS;
          } catch (error) {
            if (disposed) return;
            if (isTerminalSyncError(error)) {
              handleTerminalError(error);
              return;
            }
            debt = true;
            return;
          }
        }
      })().finally(() => {
        activeSync = null;
        if (disposed || terminal || !debt || retryTimer !== null) return;
        const delayMs = retryDelayMs;
        retryDelayMs = nextRetryDelay(retryDelayMs);
        retryTimer = setTimeout(() => {
          retryTimer = null;
          void runSync();
        }, delayMs);
      });
      return await activeSync;
    };

    const requestSync = (): Promise<void> => {
      if (disposed || terminal) return Promise.resolve();
      debt = true;
      cancelRetry();
      return runSync();
    };

    const invalidationThrottler = new Throttler(
      () => {
        void queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.sessions() });
        void queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.sessionSearches() });
        void requestSync();
      },
      { leading: false, trailing: true, wait: 100 },
    );

    const unsubscribeInvalidated = api.orchestration.onThreadIndexInvalidated((event) => {
      if (
        useStore.getState().threadIndexNextCursor === undefined &&
        event.revision !== undefined &&
        event.revision <= useStore.getState().threadIndexRevision
      ) {
        return;
      }
      invalidatedThreadIds.add(event.threadId);
      if (event.eventType === "thread.session-set") {
        void queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.usage() });
      }
      invalidationThrottler.maybeExecute();
    });
    const unsubscribeWelcome = onServerWelcome((payload) => {
      serverGeneration += 1;
      notifications.retryPending();
      for (const thread of useStore.getState().threads) invalidatedThreadIds.add(thread.id);
      // New rows may have arrived beyond the first page while disconnected.
      // Start a new cursor chain while retaining already loaded conversations.
      useStore.setState({ threadIndexRevision: 0, threadIndexNextCursor: undefined });
      terminal = false;
      void requestSync().then(async () => {
        if (
          disposed ||
          !payload.bootstrapProjectId ||
          !payload.bootstrapThreadId ||
          !isOperatorsIndexPathname(pathnameRef.current) ||
          handledBootstrapThreadIdRef.current === payload.bootstrapThreadId
        ) {
          return;
        }
        setProjectExpanded(payload.bootstrapProjectId, true);
        await navigate({
          to: "/operators/$threadId",
          params: { threadId: payload.bootstrapThreadId },
          replace: true,
        });
        handledBootstrapThreadIdRef.current = payload.bootstrapThreadId;
      });
    });

    return () => {
      disposed = true;
      notifications.dispose();
      window.removeEventListener("online", notifications.retryPending);
      window.removeEventListener("focus", notifications.retryPending);
      for (const toast of attentionToasts.values()) toastManager.close(toast.id);
      presence?.dispose();
      if (presenceRef.current === presence) presenceRef.current = null;
      abortController.abort();
      cancelRetry();
      invalidationThrottler.cancel();
      unsubscribeInvalidated();
      unsubscribeWelcome();
    };
  }, [
    currentUserEmail,
    navigate,
    queryClient,
    removeOrphanedTerminalStates,
    setProjectExpanded,
    syncThreadIndex,
  ]);

  return null;
}

function ActiveThreadCoordinator() {
  const applyDomainEvent = useStore((store) => store.applyDomainEvent);
  const syncThreadDetail = useStore((store) => store.syncThreadDetail);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [connectionGeneration, setConnectionGeneration] = useState(0);
  const desiredThreadId = useRouterState({
    select: (state) => {
      const threadMatch = state.matches.find(
        (match) => match.routeId === "/_app/operators/$threadId",
      );
      const threadId = threadMatch?.params.threadId;
      return typeof threadId === "string" ? asThreadId(threadId) : null;
    },
  });

  useEffect(
    () =>
      onServerWelcome(() => {
        setConnectionGeneration((generation) => generation + 1);
      }),
    [],
  );

  useEffect(() => {
    const api = readNativeApi();
    if (!api || !desiredThreadId || connectionGeneration === 0) return;

    const isLocalDraft = Object.hasOwn(
      useComposerDraftStore.getState().draftThreadsByThreadId,
      desiredThreadId,
    );
    const cachedThread = useStore
      .getState()
      .threads.find((thread) => thread.id === desiredThreadId);
    if (
      isLocalDraft ||
      (cachedThread !== undefined &&
        cachedThread.deletedAt == null &&
        cachedThread.session === null)
    ) {
      return;
    }

    let disposed = false;
    let terminal = false;
    let debt = true;
    let hydrating = false;
    let replaying = false;
    let replayDebt = false;
    let activeSync: Promise<void> | null = null;
    let activeReplay: Promise<void> | null = null;
    let retryDelayMs = SYNC_RETRY_INITIAL_MS;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let streamSequence = 0;
    let bufferedEvents: OrchestrationEvent[] = [];
    let bufferOverflowed = false;
    let observationLeaseId: string | undefined;
    const abortController = new AbortController();

    const cancelRetry = () => {
      if (retryTimer !== null) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
    };

    const handleTerminalError = (error: WsRequestError) => {
      terminal = true;
      debt = false;
      cancelRetry();
      if (error.code === "unauthorized") {
        notifyOperatorsAuthExpired(error.message);
        return;
      }
      void navigate({ to: "/operators", replace: true });
    };

    const validateDetail = (detail: OrchestrationThreadDetail) => {
      if (detail.threadId !== desiredThreadId || detail.thread.id !== desiredThreadId) {
        throw new Error(`Thread detail did not match ${desiredThreadId}.`);
      }
    };

    const applyIncrementalEvent = (event: OrchestrationEvent) => {
      applyDomainEvent(event);
      if (event.type === "thread.message-sent" && !event.payload.streaming) {
        void queryClient.invalidateQueries({ queryKey: operatorsQueryKeys.sessionSearches() });
      }
      if (
        event.type === "thread.activity-appended" &&
        event.payload.activity.kind === "file.changed"
      ) {
        void queryClient.invalidateQueries({ queryKey: providerQueryKeys.all });
        void queryClient.invalidateQueries({ queryKey: projectQueryKeys.all });
      }
    };

    const runSync = async (): Promise<void> => {
      if (activeSync || disposed || terminal) return await (activeSync ?? Promise.resolve());
      activeSync = (async () => {
        while (debt) {
          if (disposed || terminal) return;
          debt = false;
          hydrating = true;
          bufferedEvents = [];
          bufferOverflowed = false;
          try {
            const requestedLeaseId = globalThis.crypto.randomUUID();
            observationLeaseId = requestedLeaseId;
            const detail = await openOperatorsThread(desiredThreadId, {
              observationLeaseId: requestedLeaseId,
              signal: abortController.signal,
            });
            if (disposed) return;
            validateDetail(detail);
            if (detail.observationLeaseId !== requestedLeaseId) {
              throw new Error(`Task observation lease did not match ${desiredThreadId}.`);
            }
            // Display the consistent snapshot immediately. Keep buffering live
            // events until replay catches up so they cannot race with hydration.
            syncThreadDetail(detail);
            streamSequence = detail.streamSequence;
            const replay = await api.orchestration.replayEvents({
              fromSequenceExclusive: detail.streamSequence,
              threadId: desiredThreadId,
            });
            if (disposed) return;

            const events = mergeOrchestrationEvents(replay.events, bufferedEvents).filter(
              (event) =>
                event.aggregateKind === "thread" &&
                event.aggregateId === desiredThreadId &&
                event.sequence > detail.streamSequence,
            );
            if (
              replay.truncated ||
              bufferOverflowed ||
              events.some((event) => !isIncrementalThreadEvent(event))
            ) {
              debt = true;
              continue;
            }

            bufferedEvents = [];
            for (const event of events) {
              applyIncrementalEvent(event);
            }
            streamSequence = Math.max(
              detail.streamSequence,
              replay.headSequence,
              ...events.map((event) => event.sequence),
            );
            retryDelayMs = SYNC_RETRY_INITIAL_MS;
          } catch (error) {
            if (disposed) return;
            if (isTerminalSyncError(error)) {
              handleTerminalError(error);
              return;
            }
            debt = true;
            return;
          } finally {
            hydrating = false;
          }
        }
      })().finally(() => {
        activeSync = null;
        if (!disposed && !terminal && !debt && bufferedEvents.length > 0) {
          replayDebt = true;
          void runReplay();
          return;
        }
        if (disposed || terminal || !debt || retryTimer !== null) return;
        const delayMs = retryDelayMs;
        retryDelayMs = nextRetryDelay(retryDelayMs);
        retryTimer = setTimeout(() => {
          retryTimer = null;
          void runSync();
        }, delayMs);
      });
      return await activeSync;
    };

    const requestSync = () => {
      if (disposed || terminal) return;
      debt = true;
      cancelRetry();
      void runSync();
    };

    const runReplay = async (): Promise<void> => {
      if (activeReplay || disposed || terminal || hydrating || debt) {
        return await (activeReplay ?? Promise.resolve());
      }
      activeReplay = (async () => {
        replaying = true;
        try {
          while (replayDebt || bufferedEvents.length > 0) {
            if (debt || hydrating) {
              break;
            }
            replayDebt = false;
            const replay = await api.orchestration.replayEvents({
              fromSequenceExclusive: streamSequence,
              threadId: desiredThreadId,
            });
            if (disposed || debt || hydrating) return;
            const events = mergeOrchestrationEvents(replay.events, bufferedEvents).filter(
              (event) =>
                event.aggregateKind === "thread" &&
                event.aggregateId === desiredThreadId &&
                event.sequence > streamSequence,
            );
            bufferedEvents = [];
            if (
              replay.truncated ||
              bufferOverflowed ||
              events.some((event) => !isIncrementalThreadEvent(event))
            ) {
              requestSync();
              return;
            }
            for (const event of events) {
              applyIncrementalEvent(event);
            }
            streamSequence = Math.max(
              streamSequence,
              replay.headSequence,
              ...events.map((event) => event.sequence),
            );
          }
        } catch (error) {
          if (disposed) return;
          if (isTerminalSyncError(error)) {
            handleTerminalError(error);
            return;
          }
          requestSync();
        } finally {
          replaying = false;
        }
      })().finally(() => {
        activeReplay = null;
        if (!disposed && !terminal && !debt && (replayDebt || bufferedEvents.length > 0)) {
          void runReplay();
        }
      });
      return await activeReplay;
    };

    const requestReplay = () => {
      if (disposed || terminal || debt) return;
      replayDebt = true;
      void runReplay();
    };

    const unsubscribeDomainEvent = api.orchestration.onDomainEvent((event) => {
      const route = routeActiveThreadEvent({
        busy: hydrating || replaying || debt,
        event,
        streamSequence,
        threadId: desiredThreadId,
      });
      switch (route) {
        case "apply":
          applyIncrementalEvent(event);
          streamSequence = event.sequence;
          return;
        case "buffer":
        case "replay":
          if (bufferedEvents.length < MAX_BUFFERED_THREAD_EVENTS) {
            bufferedEvents.push(event);
          } else {
            bufferOverflowed = true;
          }
          if (route === "replay") {
            requestReplay();
          }
          return;
        case "snapshot":
          requestSync();
          return;
        case "ignore":
          return;
      }
    });

    void runSync();
    return () => {
      disposed = true;
      abortController.abort();
      cancelRetry();
      unsubscribeDomainEvent();
      // Do not await the close: a route change must be able to open the new
      // task immediately, and the server uses this request to abort stale work.
      void closeOperatorsThread(desiredThreadId, observationLeaseId).catch(() => undefined);
    };
  }, [
    applyDomainEvent,
    connectionGeneration,
    desiredThreadId,
    navigate,
    queryClient,
    syncThreadDetail,
  ]);

  return null;
}
